import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import { storeTasksAPI, StoreTask, StoreTaskItem } from '../../services/millAPI';
import StoreHeader from '../../components/Layout/StoreHeader';

const StoreTasks: React.FC = () => {
  const { isAuthenticated } = useStoreAuth();
  const navigate = useNavigate();

  const [tasks, setTasks] = useState<StoreTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTask, setSelectedTask] = useState<StoreTask | null>(null);
  const [pullingItem, setPullingItem] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }
    loadTasks();
  }, [isAuthenticated, navigate]);

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await storeTasksAPI.getList(statusFilter || undefined);
      setTasks(response.data.tasks || []);
    } catch (err: any) {
      console.error('Error loading tasks:', err);
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (isAuthenticated) {
      loadTasks();
    }
  }, [loadTasks, isAuthenticated]);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleStartTask = async (taskId: number) => {
    try {
      await storeTasksAPI.start(taskId);
      showSuccess('Task started');
      loadTasks();
    } catch (err: any) {
      console.error('Error starting task:', err);
      alert('Failed to start task: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleMarkItemPulled = async (taskId: number, itemId: number) => {
    try {
      setPullingItem(itemId);
      const response = await storeTasksAPI.markItemPulled(taskId, itemId);
      showSuccess(response.data.message || 'Item marked as pulled');

      // Update local state
      setTasks(prevTasks =>
        prevTasks.map(task => {
          if (task.id === taskId) {
            return {
              ...task,
              pulled_items: task.pulled_items + 1,
              status: response.data.task_status || task.status,
              is_complete: response.data.task_complete || task.is_complete,
              items: task.items.map(item =>
                item.id === itemId ? { ...item, is_pulled: true } : item
              ),
            };
          }
          return task;
        })
      );

      // Also update selected task if viewing
      if (selectedTask && selectedTask.id === taskId) {
        setSelectedTask(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            pulled_items: prev.pulled_items + 1,
            status: response.data.task_status || prev.status,
            is_complete: response.data.task_complete || prev.is_complete,
            items: prev.items.map(item =>
              item.id === itemId ? { ...item, is_pulled: true } : item
            ),
          };
        });
      }
    } catch (err: any) {
      console.error('Error marking item pulled:', err);
      alert('Failed to mark item as pulled: ' + (err.response?.data?.error || err.message));
    } finally {
      setPullingItem(null);
    }
  };

  const handleCompleteTask = async (taskId: number) => {
    try {
      await storeTasksAPI.complete(taskId);
      showSuccess('Task completed');
      setSelectedTask(null);
      loadTasks();
    } catch (err: any) {
      console.error('Error completing task:', err);
      alert('Failed to complete task: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancelTask = async (taskId: number) => {
    if (!window.confirm('Are you sure you want to cancel this task? This will release any reserved inventory.')) {
      return;
    }
    try {
      await storeTasksAPI.cancel(taskId);
      showSuccess('Task cancelled');
      setSelectedTask(null);
      loadTasks();
    } catch (err: any) {
      console.error('Error cancelling task:', err);
      alert('Failed to cancel task: ' + (err.response?.data?.error || err.message));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'status-pending';
      case 'IN_PROGRESS':
        return 'status-in-progress';
      case 'COMPLETED':
        return 'status-completed';
      case 'CANCELLED':
        return 'status-cancelled';
      default:
        return '';
    }
  };

  const getPriorityClass = (priority: string) => {
    return priority === 'URGENT' ? 'priority-urgent' : 'priority-normal';
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="store-page">
        <StoreHeader />
        <div className="store-loading">
          <div className="loading-spinner"></div>
          <p>Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="store-page">
      <StoreHeader />

      {/* Success Toast */}
      {successMessage && (
        <div className="store-toast store-toast-success">
          {successMessage}
        </div>
      )}

      <div className="store-content">
        {error && <div className="store-alert store-alert-error">{error}</div>}

        {/* Filters */}
        <div className="store-filters">
          <div className="filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">Active (Pending/In Progress)</option>
              <option value="PENDING">Pending Only</option>
              <option value="IN_PROGRESS">In Progress Only</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
          <button className="store-btn store-btn-secondary" onClick={loadTasks}>
            Refresh
          </button>
        </div>

        {/* Tasks List */}
        {tasks.length === 0 ? (
          <div className="store-empty-state">
            <div className="empty-icon">&#10003;</div>
            <h3>No Tasks</h3>
            <p>There are no {statusFilter ? statusFilter.toLowerCase().replace('_', ' ') : 'active'} tasks at the moment.</p>
          </div>
        ) : (
          <div className="store-tasks-grid">
            {tasks.map(task => (
              <div
                key={task.id}
                className={`store-task-card ${getStatusClass(task.status)} ${getPriorityClass(task.priority)}`}
                onClick={() => setSelectedTask(task)}
              >
                <div className="task-card-header">
                  <span className={`task-type ${task.task_type.toLowerCase()}`}>
                    {task.task_type === 'PULL_BAGGED' ? '📦 Bagged' : '🥄 BYOC'}
                  </span>
                  <span className={`task-priority ${getPriorityClass(task.priority)}`}>
                    {task.priority === 'URGENT' && '⚠️ '}{task.priority_display}
                  </span>
                </div>

                <div className="task-card-body">
                  <div className="task-due-date">
                    <strong>Due:</strong> {formatDate(task.due_date)}
                  </div>
                  <div className="task-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${(task.pulled_items / task.total_items) * 100}%` }}
                      ></div>
                    </div>
                    <span className="progress-text">
                      {task.pulled_items} / {task.total_items} items pulled
                    </span>
                  </div>
                  <div className="task-items-preview">
                    {task.items.slice(0, 3).map((item, idx) => (
                      <span key={idx} className={`item-preview ${item.is_pulled ? 'pulled' : ''}`}>
                        {item.is_pulled ? '✓' : '○'} {item.feed_formula_name}
                        {item.quantity_bags && ` (${item.quantity_bags})`}
                      </span>
                    ))}
                    {task.items.length > 3 && (
                      <span className="more-items">+{task.items.length - 3} more</span>
                    )}
                  </div>
                </div>

                <div className="task-card-footer">
                  <span className={`task-status ${getStatusClass(task.status)}`}>
                    {task.status_display}
                  </span>
                  {task.status === 'PENDING' && (
                    <button
                      className="store-btn store-btn-sm store-btn-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartTask(task.id);
                      }}
                    >
                      Start
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="store-modal-overlay" onClick={() => setSelectedTask(null)}>
          <div className="store-modal" onClick={(e) => e.stopPropagation()}>
            <div className="store-modal-header">
              <h2>
                {selectedTask.task_type === 'PULL_BAGGED' ? '📦 Pull Bagged Inventory' : '🥄 Pull from BYOC Bin'}
              </h2>
              <button className="modal-close" onClick={() => setSelectedTask(null)}>&times;</button>
            </div>

            <div className="store-modal-body">
              <div className="task-detail-info">
                <div className="info-row">
                  <span className="info-label">Due Date:</span>
                  <span className="info-value">{formatDate(selectedTask.due_date)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Priority:</span>
                  <span className={`info-value ${getPriorityClass(selectedTask.priority)}`}>
                    {selectedTask.priority_display}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Status:</span>
                  <span className={`info-value ${getStatusClass(selectedTask.status)}`}>
                    {selectedTask.status_display}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Progress:</span>
                  <span className="info-value">
                    {selectedTask.pulled_items} / {selectedTask.total_items} items pulled
                  </span>
                </div>
              </div>

              <h3 className="items-header">Items to Pull:</h3>
              <div className="task-items-list">
                {selectedTask.items.map(item => (
                  <div
                    key={item.id}
                    className={`task-item-row ${item.is_pulled ? 'pulled' : ''}`}
                  >
                    <div className="item-details">
                      <div className="item-main">
                        <span className="item-formula">{item.feed_formula_name}</span>
                        <span className={`item-form form-${item.feed_form.toLowerCase()}`}>
                          {item.feed_form}
                        </span>
                      </div>
                      <div className="item-quantity">
                        {item.quantity_bags ? (
                          <span><strong>{item.quantity_bags}</strong> {item.bag_size} bags</span>
                        ) : (
                          <span><strong>{item.quantity_lbs.toLocaleString()}</strong> lbs</span>
                        )}
                      </div>
                      <div className="item-order">
                        For: <strong>#{item.order_number}</strong> - {item.customer_name}
                      </div>
                    </div>
                    <div className="item-action">
                      {item.is_pulled ? (
                        <span className="pulled-badge">&#10003; Pulled</span>
                      ) : (
                        <button
                          className="store-btn store-btn-sm store-btn-success"
                          onClick={() => handleMarkItemPulled(selectedTask.id, item.id)}
                          disabled={pullingItem === item.id}
                        >
                          {pullingItem === item.id ? 'Marking...' : 'Mark Pulled'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {selectedTask.notes && (
                <div className="task-notes">
                  <h4>Notes:</h4>
                  <p>{selectedTask.notes}</p>
                </div>
              )}
            </div>

            <div className="store-modal-footer">
              {selectedTask.status !== 'COMPLETED' && selectedTask.status !== 'CANCELLED' && (
                <>
                  {selectedTask.is_complete && (
                    <button
                      className="store-btn store-btn-success"
                      onClick={() => handleCompleteTask(selectedTask.id)}
                    >
                      Complete Task
                    </button>
                  )}
                  <button
                    className="store-btn store-btn-danger"
                    onClick={() => handleCancelTask(selectedTask.id)}
                  >
                    Cancel Task
                  </button>
                </>
              )}
              <button
                className="store-btn store-btn-secondary"
                onClick={() => setSelectedTask(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .store-tasks-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1rem;
          padding: 1rem;
        }

        .store-task-card {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          padding: 1rem;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
          border-left: 4px solid #ccc;
        }

        .store-task-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }

        .store-task-card.priority-urgent {
          border-left-color: #f44336;
        }

        .store-task-card.status-pending {
          border-left-color: #ff9800;
        }

        .store-task-card.status-in-progress {
          border-left-color: #2196f3;
        }

        .store-task-card.status-completed {
          border-left-color: #4caf50;
          opacity: 0.7;
        }

        .task-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .task-type {
          font-weight: 600;
          font-size: 0.9rem;
        }

        .task-priority.priority-urgent {
          color: #f44336;
          font-weight: 600;
        }

        .task-due-date {
          margin-bottom: 0.5rem;
          color: #666;
        }

        .task-progress {
          margin-bottom: 0.75rem;
        }

        .progress-bar {
          height: 8px;
          background: #e0e0e0;
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 0.25rem;
        }

        .progress-fill {
          height: 100%;
          background: #4caf50;
          transition: width 0.3s;
        }

        .progress-text {
          font-size: 0.8rem;
          color: #666;
        }

        .task-items-preview {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          font-size: 0.85rem;
        }

        .item-preview {
          color: #666;
        }

        .item-preview.pulled {
          color: #4caf50;
          text-decoration: line-through;
        }

        .more-items {
          color: #999;
          font-style: italic;
        }

        .task-card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid #eee;
        }

        .task-status {
          font-size: 0.8rem;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-weight: 500;
        }

        .task-status.status-pending {
          background: #fff3e0;
          color: #f57c00;
        }

        .task-status.status-in-progress {
          background: #e3f2fd;
          color: #1976d2;
        }

        .task-status.status-completed {
          background: #e8f5e9;
          color: #388e3c;
        }

        .task-items-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          max-height: 400px;
          overflow-y: auto;
        }

        .task-item-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          background: #f9f9f9;
          border-radius: 6px;
          border-left: 3px solid #2196f3;
        }

        .task-item-row.pulled {
          background: #e8f5e9;
          border-left-color: #4caf50;
        }

        .item-main {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.25rem;
        }

        .item-formula {
          font-weight: 600;
        }

        .item-form {
          font-size: 0.75rem;
          padding: 0.15rem 0.4rem;
          border-radius: 3px;
          background: #e0e0e0;
        }

        .item-form.form-mash {
          background: #fff3e0;
          color: #e65100;
        }

        .item-form.form-pellet {
          background: #e3f2fd;
          color: #1565c0;
        }

        .item-quantity {
          font-size: 0.9rem;
          color: #666;
        }

        .item-order {
          font-size: 0.8rem;
          color: #999;
        }

        .pulled-badge {
          color: #4caf50;
          font-weight: 600;
        }

        .store-filters {
          display: flex;
          gap: 1rem;
          padding: 1rem;
          background: #f5f5f5;
          border-radius: 8px;
          margin: 1rem;
          align-items: center;
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .filter-select {
          padding: 0.5rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 0.9rem;
        }

        .store-empty-state {
          text-align: center;
          padding: 3rem;
          color: #666;
        }

        .empty-icon {
          font-size: 3rem;
          color: #4caf50;
          margin-bottom: 1rem;
        }

        .items-header {
          margin: 1rem 0 0.75rem;
          font-size: 1rem;
          color: #333;
        }

        .task-detail-info {
          background: #f5f5f5;
          padding: 1rem;
          border-radius: 6px;
          margin-bottom: 1rem;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0;
          border-bottom: 1px solid #e0e0e0;
        }

        .info-row:last-child {
          border-bottom: none;
        }

        .info-label {
          color: #666;
        }

        .info-value {
          font-weight: 500;
        }

        .store-toast {
          position: fixed;
          top: 80px;
          right: 20px;
          padding: 1rem 1.5rem;
          background: #4caf50;
          color: white;
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
          z-index: 1000;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .store-btn-danger {
          background: #f44336;
          color: white;
          border: none;
        }

        .store-btn-danger:hover {
          background: #d32f2f;
        }
      `}</style>
    </div>
  );
};

export default StoreTasks;
